import { isEmpty } from 'ramda';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { videoSeekFallback, ENTITIES, MEDIA_STATUS, STREAM_TYPES, USER } from '../../consts';
import messages from '../../intl/messages';
import { getUserRole } from '../../store/auth/selectors';
import { fetchMobileUrl, fetchRoomVideoUrl, fetchScreenUrl, fetchWebcamUrl } from '../../store/model/actions';
import { getCurrentExam, getRoomVideoUrl, getScreenUrl, getWebcamUrl, getMobileUrl } from '../../store/model/selectors';
import { setVideoSeek, setVideoState } from '../../store/ui/actions';
import { getTypeFilter, getVideoSeek, getVideoState } from '../../store/ui/selectors';
import MediaAccessDeniedAlert from '../MediaAccessDeniedAlert';
import VideoControlBar from './VideoControlBar';

const useStyles = makeStyles()((theme) => ({
	root: {
		'& .examVideoWrapper': {
			minHeight: '350px',
			width: '100%',
			overflow: 'hidden',
		},
		'& .examVideo': {
			width: '100%',
			height: '350px',
			display: 'block',
			'&:focus': {
				outline: 'none',
			},
			'&:-webkit-media-controls-panel': {
				display: 'flex !important',
				opacity: '1 !important',
			},
		},
		'&.fullscreen': {
			background: 'black',
			'& .examVideo': {
				width: '100%',
				height: '88vh',
				top: 0,
				left: 0,
			},
			'& .control-bar': {
				marginTop: '-6px',
				paddingLeft: '1rem',
				paddingRight: '1rem',
				background: 'white',
				'& .MuiGrid-item': {
					paddingTop: '5px',
				},
			},
		},

		'& .videoNotFound': {
			position: 'absolute',
			top: '50%',
			textAlign: 'center',
			width: '100%',
		},

		'& .videoAccessDenied': {
			position: 'absolute',
			top: '40%',
			width: '100%',
			display: 'flex',
			justifyContent: 'center',
			padding: '0 20px',
		},
	},
}));

// play() / pause() - Fix for DOMException: The play() request was interrupted - https://goo.gl/LdLk22
const play = (videoEl) => {
	if (videoEl) {
		const lsItem = 'dontInterruptVideo' + videoEl.id;

		localStorage.setItem(lsItem, 'true');
		videoEl
			.play()
			.then(() => localStorage.removeItem(lsItem))
			.catch((e) => console.log(e));
	}
};

const pause = (videoEl) => {
	if (videoEl) {
		const lsItem = 'dontInterruptVideo' + videoEl.id;
		if (!localStorage.getItem(lsItem)) videoEl.pause();
	}
};

const VideoBox = ({ pipTypeFilter }) => {
	const dispatch = useDispatch();
	const videoOriginal = useRef();
	const wasPlaying = useRef(false);
	const { classes } = useStyles();
	const userRole = useSelector(getUserRole);
	const allowDownload = userRole !== USER.ROLES.REVIEWER && userRole !== USER.ROLES.COMMISSIONER;

	const _typeFilter = useSelector(getTypeFilter);
	const typeFilter = pipTypeFilter || _typeFilter;
	const exam = useSelector(getCurrentExam);
	const webcamUrl = useSelector(getWebcamUrl);
	const screenUrl = useSelector(getScreenUrl);
	const mobileUrl = useSelector(getMobileUrl);
	const roomVideoUrl = useSelector(getRoomVideoUrl);
	const videoSeek = useSelector(getVideoSeek);
	const videoState = useSelector(getVideoState);

	const getSrc = useCallback(() => {
		switch (typeFilter) {
			case STREAM_TYPES.SCREEN:
				return screenUrl;
			case ENTITIES.VIDEO_ROOM:
				return roomVideoUrl;
			case STREAM_TYPES.MOBILE:
				return mobileUrl;
			default:
				return webcamUrl;
		}
	}, [roomVideoUrl, screenUrl, typeFilter, webcamUrl, mobileUrl]);

	const getVideoErrorMessage = useCallback(() => {
		switch (typeFilter) {
			case STREAM_TYPES.SCREEN:
				return <FormattedMessage {...messages.screenVideoNotFound} />;
			case ENTITIES.VIDEO_ROOM:
				return <FormattedMessage {...messages.roomVideoNotFound} />;
			case STREAM_TYPES.MOBILE:
				return <FormattedMessage {...messages.mobileVideoNotFound} />;
			default:
				return <FormattedMessage {...messages.webcamVideoNotFound} />;
		}
	}, [typeFilter]);

	const [videoUrl, setVideoUrl] = useState(getSrc());
	const [dataLoaded, setDataLoaded] = useState(false);
	const [rotation, setRotation] = useState(0);

	// SAS URLs expire after a while - allow a single re-fetch of the active stream URL per exam/stream
	const urlRetried = useRef(false);
	const resumeAfterRetry = useRef(false);

	useEffect(() => {
		urlRetried.current = false;
	}, [exam?.id, typeFilter]);

	const handleVideoError = useCallback(() => {
		// Retry only once per exam/stream to avoid a refetch loop on a genuinely broken source
		if (urlRetried.current || !exam?.id || !videoUrl || isEmpty(videoUrl)) return;
		urlRetried.current = true;

		const video = videoOriginal.current;
		if (video && video.currentTime > 0) {
			resumeAfterRetry.current = videoState === 'play';
			// Keep the current position - the seek effect subtracts videoSeekFallback again
			dispatch(setVideoSeek((video.currentTime + videoSeekFallback) * 1000));
		}

		switch (typeFilter) {
			case STREAM_TYPES.SCREEN:
				dispatch(fetchScreenUrl(exam.id));
				break;
			case ENTITIES.VIDEO_ROOM:
				dispatch(fetchRoomVideoUrl(exam.id));
				break;
			case STREAM_TYPES.MOBILE:
				dispatch(fetchMobileUrl(exam.id));
				break;
			default:
				dispatch(fetchWebcamUrl(exam.id));
		}
	}, [dispatch, exam, typeFilter, videoState, videoUrl]);

	const [checkpointOffsets, setCheckpointOffsets] = useState([]);

	const handleFirstDocumentClick = useCallback(() => {
		play(videoOriginal.current);
		document.removeEventListener('click', handleFirstDocumentClick);
	}, []);

	const handleRotate = useCallback(() => {
		setRotation((prevRotation) => (prevRotation + 90) % 360);
	}, []);

	const isScreenVideo = typeFilter === STREAM_TYPES.SCREEN;

	useEffect(() => {
		if (videoOriginal.current) {
			// For screen recording (no audio), we'll use a simple approach
			if (isScreenVideo) {
				videoOriginal.current.addEventListener('pause', (event) => {
					// Only handle automatic pauses, not user-initiated ones
					if (document.visibilityState === 'hidden' && wasPlaying.current) {
						const playPromise = event.target.play();
						if (playPromise) {
							playPromise.catch(() => {
								// Ignore errors in background
							});
						}
					}
				});
			}

			const handleVisibilityChange = () => {
				if (isScreenVideo) {
					if (document.visibilityState === 'hidden') {
						wasPlaying.current = !videoOriginal.current.paused;
					} else {
						// When returning to visible state, ensure correct playback state
						if (wasPlaying.current && videoOriginal.current.paused) {
							play(videoOriginal.current);
						}
					}
				}
			};

			videoOriginal.current.onpause = () => {
				// Only dispatch pause state for user-initiated pauses or non-screen video
				if (!isScreenVideo || document.visibilityState === 'visible') {
					dispatch(setVideoState('pause'));
				}
			};
			
			videoOriginal.current.onplay = () => {
				dispatch(setVideoState('play'));
				if (isScreenVideo) {
					wasPlaying.current = true;
				}
			};

			document.addEventListener('visibilitychange', handleVisibilityChange);
			document.addEventListener('click', handleFirstDocumentClick);

			return () => {
				document.removeEventListener('visibilitychange', handleVisibilityChange);
				document.removeEventListener('click', handleFirstDocumentClick);
			};
		}
	}, [dispatch, handleFirstDocumentClick, isScreenVideo]);

	useEffect(() => {
		if (videoOriginal.current && dataLoaded) {
			const seekInSecs = Math.floor(videoSeek / 1000);
			const seekPoint = seekInSecs - videoSeekFallback < 0 ? 0 : seekInSecs - videoSeekFallback;

			videoOriginal.current.currentTime = seekPoint || 0;
		}
	}, [videoSeek, dataLoaded]);

	useEffect(() => {
		if (videoOriginal.current) {
			if (videoState === 'play') play(videoOriginal.current);
			else pause(videoOriginal.current);
		}
	}, [videoState]);

	const handleDataLoaded = useCallback(() => {
		setDataLoaded(true);
		// Resume playback after an error-triggered URL refetch (the URL-change effect pauses)
		if (resumeAfterRetry.current) {
			resumeAfterRetry.current = false;
			dispatch(setVideoState('play'));
		}
	}, [dispatch]);

	useEffect(() => {
		setDataLoaded(false);
		setVideoUrl(getSrc());
		dispatch(setVideoState('pause'));
	}, [dispatch, getSrc, typeFilter]);

	useEffect(() => {
		if (exam) {
			const offsets = {
				id: {
					offset: Date.parse(exam.idCheckpointStartedAt) - Date.parse(exam.streamStartedAt),
					tooltip: <FormattedMessage {...messages.idCheckpointStartedAt} />,
				},
				face: {
					offset: Date.parse(exam.faceCheckpointStartedAt) - Date.parse(exam.streamStartedAt),
					tooltip: <FormattedMessage {...messages.faceCheckpointStartedAt} />,
				},
				room: {
					offset: Date.parse(exam.roomCheckpointStartedAt) - Date.parse(exam.streamStartedAt),
					tooltip: <FormattedMessage {...messages.roomCheckpointStartedAt} />,
				},
				exam: {
					offset: Date.parse(exam.examCheckpointStartedAt) - Date.parse(exam.streamStartedAt),
					tooltip: <FormattedMessage {...messages.examCheckpointStartedAt} />,
				},
			};

			if (typeFilter === STREAM_TYPES.WEBCAM || typeFilter === STREAM_TYPES.SCREEN) {
				setCheckpointOffsets([offsets.id, offsets.face, offsets.room, offsets.exam]);
			} else if (typeFilter === ENTITIES.VIDEO_ROOM) setCheckpointOffsets([offsets.id, offsets.face, offsets.room]);
		}
	}, [exam, typeFilter]);

	useEffect(() => {
		const playPauseHandler = (event) => {
			// Guard: video element doesn't exist when media is forbidden/missing (isPlayable === false)
			if (!videoOriginal.current) return;
			if (event.srcElement === document.body && (event.type === 'click' || event.code === 'Space')) {
				event.preventDefault();
				dispatch(setVideoState(videoOriginal.current.paused ? 'play' : 'pause'));
			}
		};

		// Fullscreen
		const videoContainer = document.getElementById('videoContainer');
		if (videoContainer) {
			videoContainer.onfullscreenchange = (event) => {
				if (document.fullscreenElement) videoContainer.classList.add('fullscreen');
				else videoContainer.classList.remove('fullscreen');
			};
		}

		if (!pipTypeFilter) window.addEventListener('keydown', playPauseHandler);

		if (!pipTypeFilter && videoOriginal?.current) videoOriginal.current.addEventListener('click', playPauseHandler);
		return () => {
			window.removeEventListener('keydown', playPauseHandler);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	// Prevent context menu on mouse right click
	useEffect(() => {
		if (videoOriginal.current) videoOriginal.current.addEventListener('contextmenu', (e) => e.preventDefault());
	}, [videoOriginal]);

	const isPip = !!pipTypeFilter;
	const isRotated = rotation === 90 || rotation === 270;
	const isForbidden = videoUrl === MEDIA_STATUS.FORBIDDEN;
	const isMissing = videoUrl === MEDIA_STATUS.EMPTY;
	const isPlayable = !isForbidden && !isMissing;

	const getRotationStyle = () => {
		if (isPip && isRotated) {
			const v = videoOriginal.current;
			const scale = v && v.videoWidth && v.videoHeight
				? Math.min(v.videoWidth, v.videoHeight) / Math.max(v.videoWidth, v.videoHeight)
				: 1;
			return { transform: `rotate(${rotation}deg) scale(${scale})` };
		}
		if (!isPip && isRotated) {
			return {
				transform: `rotate(${rotation}deg)`,
				objectFit: 'fill',
				width: '350px',
				height: '600px',
				margin: '-125px auto',
			};
		}
		return { transform: `rotate(${rotation}deg)` };
	};

	return (
		<div className={classes.root} id="videoContainer" style={{ position: 'relative' }}>
			<div className="examVideoWrapper">
				{isForbidden && (
					<div className="videoAccessDenied">
						<MediaAccessDeniedAlert />
					</div>
				)}

				{isMissing && <div className="videoNotFound">{getVideoErrorMessage()}</div>}

				{isPlayable && (
					<video
						className="examVideo"
						src={isEmpty(videoUrl) ? null : videoUrl}
						controls={false}
						ref={videoOriginal}
						playsInline
						disablePictureInPicture={false}
						preload="auto"
						onLoadedData={handleDataLoaded}
						onError={handleVideoError}
						style={{
							transition: 'transform 0.3s ease',
							transformOrigin: 'center center',
							...getRotationStyle(),
						}}
					/>
				)}
				<br />
				{videoOriginal.current && isPlayable && (
					<VideoControlBar
						video={videoOriginal.current}
						key={videoUrl}
						checkpointOffsets={typeFilter !== STREAM_TYPES.SCREEN ? checkpointOffsets : []}
						allowDownload={allowDownload}
						isPip={!!pipTypeFilter}
						onRotate={handleRotate}
					/>
				)}
			</div>
		</div>
	);
};

export default VideoBox;
